import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { useIsMobile } from '@/hooks/use-mobile';
import { useT } from '@/context/ContentContext';

const DESKTOP_VIDEO = 'https://cdn.shopify.com/videos/c/o/v/adf47693e8c3472085cb4223d20aca45.webm';
const MOBILE_VIDEO = 'https://cdn.shopify.com/videos/c/o/v/adf47693e8c3472085cb4223d20aca45.webm';

gsap.registerPlugin(ScrollTrigger);

const vertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  uniform float uProgress;
  uniform vec2 uResolution;
  uniform vec3 uColor;
  uniform float uSpread;
  uniform float uNoiseScale;
  varying vec2 vUv;

  float Hash(vec2 p) {
    vec3 p2 = vec3(p.xy, 1.0);
    return fract(sin(dot(p2, vec3(37.1, 61.7, 12.4))) * 3758.5453123);
  }

  float noise(in vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);

    f *= f * (3.0 - 2.0 * f);

    return mix(
      mix(Hash(i + vec2(0.0, 0.0)), Hash(i + vec2(1.0, 0.0)), f.x),
      mix(Hash(i + vec2(0.0, 1.0)), Hash(i + vec2(1.0, 1.0)), f.x),
      f.y
    );
  }

  float fbm(vec2 p) {
    float value = 0.0;
    value += noise(p * 1.0) * 0.5;
    value += noise(p * 2.0) * 0.25;
    value += noise(p * 4.0) * 0.125;
    value += noise(p * 8.0) * 0.0625;
    return value;
  }

  void main() {
    vec2 uv = vUv;

    float aspect = uResolution.x / uResolution.y;
    vec2 centeredUv = (uv - 0.5) * vec2(aspect, 1.0);

    float noiseValue = fbm(centeredUv * uNoiseScale);

    float dissolveLine = uv.y - uProgress * 1.2;
    float dissolve = dissolveLine + noiseValue * uSpread;
    float pixelSize = 1.0 / uResolution.y;
    float alpha = 1.0 - smoothstep(-pixelSize, pixelSize, dissolve);

    gl_FragColor = vec4(uColor, alpha);
  }
`;

export const Hero: React.FC = () => {
  const heroRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoSrc = isMobile ? MOBILE_VIDEO : DESKTOP_VIDEO;
  const heroTagline = useT('home.hero.tagline', 'המקום שבו חוכמת הרוקחות העתיקה פוגשת את הטכנולוגיה המתקדמת ביותר.');
  const heroCta = useT('home.hero.cta', 'גלי עוד');

  useEffect(() => {
    const hero = heroRef.current;
    const canvas = canvasRef.current;
    const container = containerRef.current;

    if (!hero || !canvas || !container) return;

    const lenis = new Lenis({
      lerp: 0.08,
      smoothWheel: true,
    });

    let lenisRafId = 0;

    const lenisRaf = (time: number) => {
      lenis.raf(time);
      ScrollTrigger.update();
      lenisRafId = requestAnimationFrame(lenisRaf);
    };

    lenisRafId = requestAnimationFrame(lenisRaf);
    lenis.on("scroll", ScrollTrigger.update);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: false,
    });

    // The mask color should match the Philosophy section bg -> #F9F7F2
    const maskColor = new THREE.Color("#f9f7f2");

    const geometry = new THREE.PlaneGeometry(2, 2);

    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      uniforms: {
        uProgress: { value: 0 },
        uResolution: {
          value: new THREE.Vector2(container.offsetWidth, container.offsetHeight),
        },
        uColor: {
          value: new THREE.Vector3(maskColor.r, maskColor.g, maskColor.b),
        },
        uSpread: { value: 0.5 },
        uNoiseScale: { value: 15.0 },
      },
    });

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const resize = () => {
      const width = container.offsetWidth;
      const height = container.offsetHeight;

      renderer.setSize(width, height, false);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

      material.uniforms.uResolution.value.set(width, height);
    };

    resize();
    window.addEventListener("resize", resize);

    let renderRafId = 0;

    const render = () => {
      renderer.render(scene, camera);
      renderRafId = requestAnimationFrame(render);
    };

    render();

    const trigger = ScrollTrigger.create({
      trigger: hero,
      start: "top top",
      end: "bottom top",
      scrub: true,
      onUpdate: (self) => {
        // Increase progress multiplier to ensure it fully dissolves before leaving viewport
        material.uniforms.uProgress.value = self.progress * 1.5;
      },
    });

    // Parallax effect for content
    const contentSelector = hero.querySelector('.hero-content-layer');

    const parallaxTrigger = ScrollTrigger.create({
      trigger: hero,
      start: "top top",
      end: "bottom top",
      scrub: true,
      animation: gsap.timeline()
        .to(contentSelector, { yPercent: -30, ease: 'none' }, 0)
    });

    // Intro animation: logo starts centered on viewport, then rises to its natural slot
    // while text + CTA fade in below.
    const logoEl = logoRef.current;
    const textEl = textRef.current;
    const ctaEl = ctaRef.current;

    let introTl: gsap.core.Timeline | null = null;

    if (logoEl && textEl && ctaEl) {
      // Compute vertical offset needed so the logo sits exactly at viewport center.
      const logoRect = logoEl.getBoundingClientRect();
      const logoCenterY = logoRect.top + logoRect.height / 2;
      const viewportCenterY = window.innerHeight / 2;
      const offset = viewportCenterY - logoCenterY;

      gsap.set(logoEl, { y: offset, opacity: 0 });
      gsap.set([textEl, ctaEl], { opacity: 0, y: 10 });

      introTl = gsap.timeline({ delay: 0.3 });
      introTl
        .to(logoEl, { opacity: 1, duration: 1.4, ease: 'power2.out' })
        .to({}, { duration: 0.6 }) // hold centered
        .to(logoEl, { y: 0, duration: 1.6, ease: 'power3.inOut' })
        .to(textEl, { opacity: 1, y: 0, duration: 1.2, ease: 'power2.out' }, '-=0.4')
        .to(ctaEl, { opacity: 1, y: 0, duration: 1.0, ease: 'power2.out' }, '-=0.6');
    }

    return () => {
      introTl?.kill();
      cancelAnimationFrame(lenisRafId);
      cancelAnimationFrame(renderRafId);

      window.removeEventListener("resize", resize);

      trigger.kill();
      parallaxTrigger.kill();
      lenis.destroy();

      scene.remove(mesh);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <section ref={heroRef} className="relative w-full h-[120vh] bg-[#412018] text-white overflow-hidden">
      <div ref={containerRef} className="absolute inset-0 w-full h-full">
        {/* Background Video */}
        <div className="absolute inset-0 z-0 hero-bg-layer">
          <video 
            key={videoSrc}
            autoPlay 
            muted 
            loop 
            playsInline
            preload="metadata"
            src={videoSrc}
            onLoadedData={() => setVideoLoaded(true)}
            onCanPlay={() => setVideoLoaded(true)}
            className={`w-full h-full object-cover transition-opacity duration-700 ${videoLoaded ? 'opacity-50' : 'opacity-0'}`}
            aria-hidden="true"
          />

          
          {/* Subtle Vignette */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#2a1a1e]/0 via-[#2a1a1e]/5 to-[#2a1a1e]/15"></div>
        </div>

        {/* Floating Arabic Calligraphy Background */}
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none overflow-hidden hero-bg-layer">
          <div className="relative w-full h-full">
              <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[25vw] leading-none font-serif text-white/5 blur-sm animate-[pulse_8s_ease-in-out_infinite] select-none tracking-tighter" style={{ fontFamily: 'serif' }}>
                ريهام
              </span>
          </div>
        </div>

        {/* --- DISSOLVE CANVAS --- */}
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full z-[15] pointer-events-none hero-canvas-layer" />

        <div className="relative z-20 container mx-auto px-6 h-screen flex flex-col justify-center items-center text-center pointer-events-none hero-content-layer">
          <div className="max-w-5xl flex flex-col items-center pointer-events-auto w-full">
            
            {/* Logo with Title & Tagline (single image) */}
            <div ref={logoRef} className="flex flex-col items-center w-72 md:w-[28rem] opacity-0 mb-6">
              <h1 className="sr-only">RIHAM — קליניקה אסתטית וטבעית של רהאם מנסור במושב פטיש</h1>
              <img
                src="https://cdn.shopify.com/s/files/1/0977/6965/2535/files/Container.png?v=1778077042"
                alt="RIHAM קליניקה טבעית ומערבית"
                width="448"
                height="431"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Description */}
            <p ref={textRef} className="max-w-xl mx-auto text-white/70 text-base md:text-xl leading-relaxed font-light tracking-wide opacity-0 mt-6">
              {heroTagline}
            </p>

            {/* CTA */}
            <div ref={ctaRef} className="flex justify-center mt-6 md:mt-12 opacity-0">
              <a href="#philosophy" className="group relative flex flex-col items-center justify-center">
                <div className="w-[1px] h-16 bg-gradient-to-b from-[#fbbf25]/0 via-[#fbbf25] to-[#fbbf25]/0 group-hover:h-24 transition-all duration-[1500ms] ease-luxury"></div>
                <span className="mt-4 text-[10px] uppercase tracking-[0.2em] text-[#fbbf25] group-hover:text-white transition-colors duration-1000">
                  {heroCta}
                </span>
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};